import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Get token from localStorage
const token = localStorage.getItem('token');

// Prevent duplicate token exchange (React Strict Mode double mount / callback re-entry)
let isProcessingAutodeskDocsTokenExchange = false;

export const getAutodeskDocsCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    // Find the Autodesk Docs master connector details
    const autodeskDocsConnector = masterConnectors.find(mc => 
      (mc.mcRecId?.toString() === mcId?.toString() || 
       mc.mcId?.toString() === mcId?.toString()) && 
      mc.mcName === "Autodesk Docs"
    );
    if (!autodeskDocsConnector) {
      throw new Error('Autodesk Docs master connector not found');
    }

    const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    if (!paramResponse.ok) {
      throw new Error('Failed to fetch connector parameters');
    }
    const connectorParams = await paramResponse.json();
    const clientId = connectorParams.find((param) => param.scpName === 'clientid')?.scpValue;
    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    const autodeskDocsAuthUrl = `https://developer.api.autodesk.com/authentication/v2/authorize?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(process.env.REACT_APP_OAUTH_REDIRECT_URI)}&state=${encodeURIComponent(connectorId)}&scope=${encodeURIComponent('account:read data:read account:write data:write')}`;

    // Check if we already have a stored authorization code
    const storedAuthCode = localStorage.getItem('AuthCode');

    if (storedAuthCode) {
      // If we have a stored authorization code, exchange it for an access token
      const tokenResponse = await exchangeAutodeskDocsAuthCodeForToken(
        storedAuthCode, 
        connectorId  // Use connector instance ID (ctd_id) for multi-connector support
      );

      // Duplicate call returned null; keep AuthCode for in-flight exchange
      if (tokenResponse == null) {
        return null;
      }

      // Clean up the stored authorization code
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingAutodeskDocsConnectorId');

      // Return the access token
      return tokenResponse;
    } else {
      // Clear stale values before starting a fresh OAuth redirect
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingAutodeskDocsConnectorId');

      // Store the connector ID for after redirect
      localStorage.setItem('PendingAutodeskDocsConnectorId', connectorId);

      // Redirect to Autodesk authentication
      window.location.href = autodeskDocsAuthUrl;
      // Return null since we're redirecting
      return null;
    }
  } catch (error) {
    console.error('Error getting Autodesk Docs credentials:', error);
    throw error;
  }
};

// Helper function to exchange authorization code for Autodesk Docs token
// Note: mcId parameter is actually connectorId (ctdId) - connector instance ID
export const exchangeAutodeskDocsAuthCodeForToken = async (authCode, mcId) => {
  if (isProcessingAutodeskDocsTokenExchange) {
    console.warn('Autodesk Docs token exchange already in progress; skipping duplicate to avoid auth code reuse / invalid_grant');
    return null;
  }

  isProcessingAutodeskDocsTokenExchange = true;

  try {
    // Note: Backend parameter is named "mcId" for compatibility, but it's actually connectorId (ctdId)
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/autodesk/docs/fullTokens?mcId=${mcId}`;
    
    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: new URLSearchParams({
        code: authCode,
        grant_type: 'authorization_code',
        redirect_uri: process.env.REACT_APP_OAUTH_REDIRECT_URI
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error response:', errorText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();
    
    // Use the connector ID passed as parameter (mcId is actually the connectorId/ctdId)
    // This ensures we use the correct connector ID even if localStorage has a stale value
    const connectorId = mcId;
    
    if (connectorId) {
      // Update the connector with token data
      const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;
      
      try {
        // First, get the current connector data
        const connectorResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!connectorResponse.ok) {
          throw new Error(`Failed to fetch connector data: ${connectorResponse.status}`);
        }
        const connectorData = await connectorResponse.json();
        
        // Calculate token expiry dates
        const primaryExpiresIn = parseInt(tokenData.primary_token?.expires_in) || 3600;
        const secondaryExpiresIn = parseInt(tokenData.secondary_token?.expires_in) || 3600;
        
        // Calculate the 14-day expiry date for refresh token
        const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
        const refreshTokenExpiry = new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString();
        
        // Prepare updated connector data
        const updatedConnectorData = {
          ...connectorData,
          token: tokenData.primary_token?.access_token,
          refreshToken: tokenData.primary_token?.refresh_token,
          tokenExpiry: new Date(Date.now() + (primaryExpiresIn * 1000)).toISOString(),
          secondaryToken: tokenData.secondary_token?.access_token,
          secondaryTokenExpiry: new Date(Date.now() + (secondaryExpiresIn * 1000)).toISOString(),
          refreshUrl: "https://developer.api.autodesk.com/authentication/v2/token",
          refreshTokenExpiry: refreshTokenExpiry,
          status: 'Connected',
          connectedTime: new Date().toISOString()
        };
        
        // Make the PUT request to update the connector
        const updateResponse = await fetch(`${API_BASE_URL}/${connectorId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedConnectorData)
        });
        
        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          console.error('Update connector error:', errorText);
          throw new Error(`Failed to update connector with tokens: ${updateResponse.status}`);
        }
        
        console.log('Autodesk Docs connector updated successfully with tokens');
        // Do not reload: reload can re-run OAuth callback and exchange the same code twice (invalid_grant).
      } catch (error) {
        console.error('Error updating Autodesk Docs connector with tokens:', error);
      }
    }
    return tokenData;
  } catch (error) {
    console.error('Token exchange error:', error);
    throw error;
  } finally {
    isProcessingAutodeskDocsTokenExchange = false;
  }
};
